import { renderChannelIcon } from '@/components/atoms/Icon/utils';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { OmnichannelOrder } from '@/services/innovorder/orders/order.type';
import {
    Container,
    CustomizationContainer,
    HeaderContainer,
    HeaderTextContainer,
    IconContainer,
    ItemContainer,
    MiscContainer,
    SubtitleContainer,
    TicketContainer,
    TitleContainer,
} from './OrderDetailsTicket.style';
import OrderDetailsTicketLine from './OrderDetailsTicketLine';
import {
    aggregateCustomizations,
    calculateSum,
    computeProductItemPrice,
    formatMeasurementUnitText,
    getLoyaltyDiscounts,
    unflattenItems,
} from './utils';

type OrderDetailsTicketProps = {
    order: OmnichannelOrder;
};

const OrderDetailsTicket: React.FunctionComponent<React.PropsWithChildren<OrderDetailsTicketProps>> = ({ order }) => {
    const intl = useIntl();
    const outputItems = useMemo(
        () => unflattenItems(order.items, !!order.reportOrderContraEntry),
        [order.items, order.reportOrderContraEntry],
    );
    const taxes = useMemo(() => order.taxes.map((tax) => tax.taxRate), [order.taxes]);
    const grantTotal = useMemo(
        () => calculateSum(order.grants.map((grant) => ({ amount: grant.amount * Math.abs(grant.quantity) }))),
        [order.grants],
    );
    const entranceFeeTotal = useMemo(
        () =>
            calculateSum(
                order.entranceFees.map((entranceFee) => ({
                    amount: entranceFee.amount * Math.abs(entranceFee.quantity),
                })),
            ),
        [order.entranceFees],
    );
    const feeTotal = useMemo(() => calculateSum(order.fees), [order.fees]);
    const loyaltyDiscounts = useMemo(() => getLoyaltyDiscounts(order.orderRecapRawData), [order.orderRecapRawData]);
    const itemTotal =
        order.totalPriceDiscountedWithTaxIncluded + grantTotal - feeTotal + order.totalDiscount - entranceFeeTotal;

    return (
        <Container>
            <HeaderContainer>
                <IconContainer>{renderChannelIcon(order.channelId)}</IconContainer>
                <HeaderTextContainer>
                    <TitleContainer>
                        {`#${order.dailyOrderId} `}
                        <FormattedMessage id={`order.consumptionMode.${order.consumptionModeId}`} />
                    </TitleContainer>
                    {order.marketplaceStoreName && (
                        <SubtitleContainer>
                            <FormattedMessage id={`order.channel.${order.channelId}`} />
                            {` - ${order.marketplaceStoreName}`}
                        </SubtitleContainer>
                    )}
                </HeaderTextContainer>
            </HeaderContainer>
            <TicketContainer>
                {outputItems.map((item, itemIndex) => (
                    <ItemContainer key={itemIndex}>
                        <OrderDetailsTicketLine
                            type="item"
                            quantity={item.quantity}
                            text={item.name}
                            price={computeProductItemPrice(item)}
                            tax={item.vatValue}
                            taxes={taxes}
                            currency={order.currency}
                        />
                        {item.measurementUnit &&
                            typeof item.measuredQuantity === 'number' &&
                            item.measuredQuantity !== 0 && (
                                <OrderDetailsTicketLine
                                    type="sum"
                                    text={formatMeasurementUnitText(item, intl, order.currency)}
                                    noTranslation
                                    currency={order.currency}
                                />
                            )}
                        {Boolean(item.customization.length) && (
                            <CustomizationContainer>
                                {aggregateCustomizations(item.customization, item.quantity).map((customization) => (
                                    <div key={customization.name}>
                                        {Math.abs(customization.quantity) > 1 && `${customization.quantity}x `}
                                        {customization.name}
                                    </div>
                                ))}
                            </CustomizationContainer>
                        )}
                    </ItemContainer>
                ))}
                <OrderDetailsTicketLine
                    type="sum"
                    text="orderDetail.ticket.itemSum"
                    price={itemTotal}
                    currency={order.currency}
                />
                {Boolean(loyaltyDiscounts.length) && (
                    <MiscContainer>
                        {loyaltyDiscounts.map((discount) => (
                            <OrderDetailsTicketLine
                                key={discount.label}
                                type="misc"
                                text={discount.label}
                                price={discount.amount}
                                noTranslation
                                currency={order.currency}
                            />
                        ))}
                    </MiscContainer>
                )}
                {Boolean(order.entranceFees?.length) && (
                    <MiscContainer>
                        {order.entranceFees.map((fee) => (
                            <OrderDetailsTicketLine
                                key={fee.labelTicket}
                                type="misc"
                                text={fee.labelTicket}
                                price={fee.amount * Math.abs(fee.quantity)}
                                unitPrice={fee.amount}
                                quantity={fee.quantity}
                                tax={fee.vat}
                                taxes={taxes}
                                noTranslation
                                currency={order.currency}
                            />
                        ))}
                        <OrderDetailsTicketLine
                            type="sum"
                            text="orderDetail.ticket.entranceFeeSum"
                            price={entranceFeeTotal}
                            currency={order.currency}
                        />
                    </MiscContainer>
                )}
                {Boolean(order.grants?.length) && (
                    <MiscContainer>
                        {order.grants.map((grant) => (
                            <OrderDetailsTicketLine
                                key={grant.labelTicket}
                                type="misc"
                                text={grant.labelTicket}
                                price={grant.amount * Math.abs(grant.quantity)}
                                unitPrice={grant.amount}
                                quantity={grant.quantity}
                                noTranslation
                                currency={order.currency}
                            />
                        ))}
                        <OrderDetailsTicketLine
                            type="sum"
                            text="orderDetail.ticket.grantSum"
                            price={grantTotal}
                            currency={order.currency}
                        />
                    </MiscContainer>
                )}
                {Boolean(order.pricingRule?.code) && (
                    <MiscContainer>
                        <OrderDetailsTicketLine
                            type="sum"
                            text="orderDetail.ticket.pricingRule"
                            code={order.pricingRule?.code}
                            currency={order.currency}
                        />
                    </MiscContainer>
                )}
                {Boolean(order.discounts?.length || Boolean(order.totalDiscount)) && (
                    <MiscContainer>
                        {order.discounts.map((discount) => (
                            <OrderDetailsTicketLine
                                key={discount.name}
                                type="misc"
                                text={intl.formatMessage({ id: 'orderDetail.ticket.discount' })}
                                code={discount.name}
                                noTranslation
                                currency={order.currency}
                            />
                        ))}
                        <OrderDetailsTicketLine
                            type="sum"
                            text="orderDetail.ticket.discountSum"
                            price={order.totalDiscount}
                            currency={order.currency}
                        />
                    </MiscContainer>
                )}
                {Boolean(order.fees?.length) && (
                    <MiscContainer>
                        {order.fees.map((fee) => (
                            <OrderDetailsTicketLine
                                key={fee.name}
                                type="misc"
                                quantity={fee.quantity}
                                text={fee.name}
                                price={fee.amount}
                                tax={fee.taxRate}
                                taxes={taxes}
                                noTranslation
                                currency={order.currency}
                            />
                        ))}
                    </MiscContainer>
                )}
                <OrderDetailsTicketLine
                    type="finalprice"
                    text="orderDetail.ticket.finalPrice"
                    price={order.totalPriceDiscountedWithTaxIncluded}
                    currency={order.currency}
                />
            </TicketContainer>
        </Container>
    );
};

export default OrderDetailsTicket;
